/** @jsx jsx */
import type { ReactNode } from 'react';
import { styled, jsx } from '@compiled/react';
import { xcss, Grid } from '@atlaskit/primitives';
import {
	ExperienceErrorBoundary,
	ExperienceSuccess,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import {
	Tokens,
	HORIZONTAL_NAV_HEIGHT,
	BUSINESS_NAV_HEIGHT,
} from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import {
	UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH,
	UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH,
	UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH,
	UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH,
} from '@atlassian/jira-navigation-system/src/index.tsx';
import { VIEW_NAV_EXPERIENCE } from '../../constants.tsx';
import {
	PerformanceMark,
	PerformanceReport,
	RENDER_START_MARK,
	RENDER_END_MARK,
} from './performance-metrics/index.tsx';

const Z_INDEX = 11;

const TEST_ID = 'business-horizontal-nav.ui.horizontal-nav';

type Props = {
	children: ReactNode;
};

const NavWrapper = ({ children }: Props) => (
	<ExperienceErrorBoundary experience={VIEW_NAV_EXPERIENCE} shouldStartExperience>
		<PerformanceMark id={RENDER_START_MARK} />

		<StyledHeader id="ak-project-view-navigation" data-testid={TEST_ID}>
			<Grid gap="space.050" alignItems="end" xcss={contentOldStyles}>
				{children}
			</Grid>
		</StyledHeader>

		<ExperienceSuccess experience={VIEW_NAV_EXPERIENCE} />
		<PerformanceMark id={RENDER_END_MARK} />
		<PerformanceReport />
	</ExperienceErrorBoundary>
);

export default NavWrapper;

const contentOldStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${HORIZONTAL_NAV_HEIGHT}px`,
	paddingTop: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const StyledHeader = styled.header({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	background: Tokens.ELEVATION_SURFACE_CONTRAST,
	display: 'grid',
	position: 'fixed',
	zIndex: Z_INDEX,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `calc(100vw - ${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH})`,
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: BUSINESS_NAV_HEIGHT,
	/* Animation matches the BusinessLayoutMainWrapper component in spa/content */
	transition:
		'width 300ms cubic-bezier(0.2, 0, 0, 1) 0s, height 300ms cubic-bezier(0.2, 0, 0, 1) 0s',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backdropFilter: Tokens.BACKDROP_FILTER,
});
